import React, { useEffect } from "react";
import { useInputChange } from "hooks/useInputChange";
import { Trans } from "@lingui/macro";
import { Button } from "components/UI/Button/Button";
import { useDispatch, useSelector } from "react-redux";

import {
  IdHandoverRecordsState,
  typeHandoverRecordFilters,
} from "model/dashboardHandoverRecord/dHandoverRecordReducer";

import { dHRecordSetFiltersAC } from "model/dashboardHandoverRecord/dHandoverRecordActionCreators";
import { useInputChangeNumberOnly } from "hooks/useInputChangeNumberOnly";
import DateFilter from "components/UI/DateFilter/DateFilter";
import { InputNumberLimit } from "../../../../UI/InputNumberLimit/InputNumberLimit";

// Type
export type typeDHandoverRecordsFilters = {
  setOpenFilterBar: React.Dispatch<React.SetStateAction<boolean>>;
  setIsActiveFilters: React.Dispatch<React.SetStateAction<boolean>>;
};

// Component
export const DHandoverRecordsFilters: React.FC<typeDHandoverRecordsFilters> = ({
  setOpenFilterBar,
  setIsActiveFilters,
}) => {
  const dispatch = useDispatch();

  const filters = useSelector(
    (state: { dHRecord: IdHandoverRecordsState }) => state.dHRecord.filters,
  );

  const { value: posNumber, setValue: setPosNumberValue } =
    useInputChangeNumberOnly(filters.posNumber || "");

  useEffect(() => {
    if (posNumber.length > 6) {
      setPosNumberValue(posNumber.substring(0, 6));
    }

    // eslint-disable-next-line
  }, [posNumber]);

  const { value: dateFromValue, setValue: setDateFromValue } = useInputChange(
    filters.closeDate?.from?.substring(0, 10) || "",
  );
  const { value: dateToValue, setValue: setDateToValue } = useInputChange(
    filters.closeDate?.to?.substring(0, 10) || "",
  );

  const checkIsChangedFiltersHandler = (
    newFilters: typeHandoverRecordFilters,
  ): boolean => {
    return !(
      newFilters.posNumber === filters.posNumber &&
      newFilters.closeDate?.to === filters.closeDate?.to &&
      newFilters.closeDate?.from === filters.closeDate?.from
    );
  };

  const onSubmitHandler = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    let a = dateFromValue
      ? new Date(dateFromValue).toISOString().slice(0, -1) + "+00:00"
      : null;
    let b = dateToValue
      ? new Date(dateToValue).toISOString().slice(0, 11) +
        "23:59:59:99.999+00:00"
      : null;

    const newFilters: typeHandoverRecordFilters = {
      posNumber: posNumber.trim() === "" ? null : posNumber.trim(),
      closeDate:
        a === "" && b === ""
          ? null
          : {
              from: a,
              to: b,
            },
    };

    if (checkIsChangedFiltersHandler(newFilters)) {
      await dispatch(dHRecordSetFiltersAC(newFilters));
      setIsActiveFilters(true);
    }
    setOpenFilterBar(false);
  };

  const onRestoreHandler = () => {
    setDateFromValue("");
    setDateToValue("");
    setPosNumberValue("");
    setIsActiveFilters(false);
    dispatch(dHRecordSetFiltersAC({ closeDate: null, posNumber: null }));
  };

  return (
    <>
      <form onSubmit={(e) => onSubmitHandler(e)}>
        <div className="flex flex-col gap-4">
          <div>
            <InputNumberLimit
              id="posNumber"
              name="posNumber"
              label={<Trans>Pos number</Trans>}
              value={posNumber}
              setValue={setPosNumberValue}
              digitBeforeDot={6}
            />
          </div>

          <DateFilter
            label={<Trans>Close date</Trans>}
            fromValue={dateFromValue}
            setFromValue={setDateFromValue}
            toValue={dateToValue}
            setToValue={setDateToValue}
            maxData={`${new Date().getUTCFullYear()}-${new Date().getUTCMonth() + 1 > 9 ? new Date().getUTCMonth() + 1 : "0" + (new Date().getUTCMonth() + 1)}-${new Date().getUTCDate()}`}
            minData={"2020-01-01"}
          />

          <div className="flex gap-4 mt-4">
            <Button
              buttonClassName="w-full"
              secondary={true}
              type="button"
              label={<Trans>Clear</Trans>}
              onClick={onRestoreHandler}
            />
            <Button
              buttonClassName="w-full"
              type="submit"
              label={<Trans>Apply</Trans>}
            />
          </div>
        </div>
      </form>
    </>
  );
};
